import { createGtm } from '@gtm-support/vue-gtm';
import { computed, createApp, defineAsyncComponent } from 'vue';

import BlockCarousel from '@shop/components/blocks/BlockCarousel.vue';
import BlockCarouselSlide from '@shop/components/blocks/BlockCarouselSlide.vue';
import BlockSlider from '@shop/components/blocks/BlockSlider.vue';
import BlockSliderItem from '@shop/components/blocks/BlockSliderItem.vue';
import FilterPills from '@shop/components/blocks/FilterPills.vue';
import ShopTheLookBlock from '@shop/components/blocks/ShopTheLookBlock.vue';
import TextCollapsable from '@shop/components/blocks/TextCollapsable.vue';
import CalfCircumferencePreselectBlock from '@shop/components/blocks/tools/CalfCircumferencePreselectBlock.vue';
import CategoryPreselectBlock from '@shop/components/blocks/tools/CategoryPreselectBlock.vue';
import CategoryPreselectBooleanBlock from '@shop/components/blocks/tools/CategoryPreselectBooleanBlock.vue';
import PadelRacketToolBlock from '@shop/components/blocks/tools/PadelRacketToolBlock.vue';
import RoofRackBlock from '@shop/components/blocks/tools/RoofRackBlock.vue';
import RoofRackPreselectBlock from '@shop/components/blocks/tools/RoofRackPreselectBlock.vue';
import SnowChainPreselectBlock from '@shop/components/blocks/tools/SnowChainPreselectBlock.vue';
import TennisRacketToolBlock from '@shop/components/blocks/tools/TennisRacketToolBlock.vue';
import YouTubeEmbed from '@shop/components/blocks/YouTubeEmbed.vue';
import ContentItemLayout from '@shop/components/content/ContentItemLayout.vue';
import Footer from '@shop/components/layout/footer/Footer.vue';
import Header from '@shop/components/layout/header/Header.vue';
import MainMenu from '@shop/components/layout/header/MainMenu.vue';
import MainMenuItem from '@shop/components/layout/header/MainMenuItem.vue';
import MainMenuSubmenu from '@shop/components/layout/header/MainMenuSubmenu.vue';
import MegaMenuPanel from '@shop/components/layout/header/MegaMenuPanel.vue';
import MobileMenu from '@shop/components/layout/header/MobileMenu.vue';
import FlashMessages from '@shop/components/ui/FlashMessages.vue';
import { generateUrl } from '@shop/utils/Routing';
import { routingReady } from '@shop/utils/Routing/routingReady';

import './assets/scss/main.scss';
import pinia from './store';
import useFlashStore from './store/flash';
import useOidcStore from './store/oidc';
import i18n from './utils/Translation';

declare global {
  interface Window {
    __etrias_cdn_base__?: (filename: string) => string;
  }

  // eslint-disable-next-line no-underscore-dangle,@typescript-eslint/naming-convention
  const __ETRIAS_CDN__: {
    stag: string;
    prod: string;
    dev: string;
    suffix_stag: string;
    suffix_dev: string;
  };
}

// eslint-disable-next-line no-underscore-dangle
window.__etrias_cdn_base__ = (filename: string): string => {
  const host = window.location.hostname;
  if (host.endsWith(__ETRIAS_CDN__.suffix_stag)) return __ETRIAS_CDN__.stag + filename;
  if (host.endsWith(__ETRIAS_CDN__.suffix_dev)) return __ETRIAS_CDN__.dev + filename;
  return __ETRIAS_CDN__.prod + filename;
};

const BannerWrapper = defineAsyncComponent(
  async () => import('@shop/components/marketing/BannerWrapper.vue')
);
const CampaignBannerPopup = defineAsyncComponent(
  async () => import('@shop/components/marketing/CampaignBannerPopup.vue')
);
const DiscountCodeCopy = defineAsyncComponent(
  async () => import('@shop/components/marketing/DiscountCodeCopy.vue')
);
const MarketingStats = defineAsyncComponent(
  async () => import('@shop/components/marketing/Stats.vue')
);
const LoginPage = defineAsyncComponent(
  async () => import('@shop/components/account/LoginPage.vue')
);
const BankDataPage = defineAsyncComponent(
  async () => import('@shop/components/payment/BankDataPage.vue')
);
const RegisterPage = defineAsyncComponent(
  async () => import('@shop/components/account/RegisterPage.vue')
);
const ForgotPasswordPage = defineAsyncComponent(
  async () => import('@shop/components/account/ForgotPasswordPage.vue')
);
const ResetPasswordPage = defineAsyncComponent(
  async () => import('@shop/components/account/ResetPasswordPage.vue')
);
const AccountDetailsPage = defineAsyncComponent(
  async () => import('@shop/components/account/AccountDetailsPage.vue')
);
const WishlistPage = defineAsyncComponent(
  async () => import('@shop/components/wishlist/WishlistPage.vue')
);
const CartPage = defineAsyncComponent(async () => import('@shop/components/cart/CartPage.vue'));
const SuccessPage = defineAsyncComponent(
  async () => import('@shop/components/checkout/SuccessPage.vue')
);
const OrdersPage = defineAsyncComponent(
  async () => import('@shop/components/account/OrdersPage.vue')
);
const OrderDetailPage = defineAsyncComponent(
  async () => import('@shop/components/account/OrderDetailPage.vue')
);
const ReturnRequestsPage = defineAsyncComponent(
  async () => import('@shop/components/account/ReturnRequestsPage.vue')
);
const ReturnRequestPage = defineAsyncComponent(
  async () => import('@shop/components/account/ReturnRequestPage.vue')
);
const ReturnRequestDetailView = defineAsyncComponent(
  async () => import('@shop/components/account/ReturnRequestDetailView.vue')
);
const ProductDetailPage = defineAsyncComponent(
  async () => import('@shop/components/product/ProductDetailPage.vue')
);
const WarrantyRemedyPage = defineAsyncComponent(
  async () => import('@shop/components/account/WarrantyRemedyPage.vue')
);
const DoubleOrderPage = defineAsyncComponent(
  async () => import('@shop/components/account/DoubleOrderPage.vue')
);
const OrderEditAddressPage = defineAsyncComponent(
  async () => import('@shop/components/account/OrderEditAddressPage.vue')
);
const SplitOrderPage = defineAsyncComponent(
  async () => import('@shop/components/account/SplitOrderPage.vue')
);
const ReturnLoginPage = defineAsyncComponent(
  async () => import('@shop/components/account/ReturnLoginPage.vue')
);
const ProductOverview = defineAsyncComponent(
  async () => import('@shop/components/productOverview/ProductOverview.vue')
);
const CheckoutPage = defineAsyncComponent(
  async () => import('@shop/components/checkout/CheckoutPage.vue')
);
const DeliveryDatePage = defineAsyncComponent(
  async () => import('@shop/components/shipping/DeliveryDatePage.vue')
);
const GiftcardSaldoPage = defineAsyncComponent(
  async () => import('@shop/components/giftcard/GiftcardSaldoPage.vue')
);

const app = createApp({
  setup() {
    const oidcStore = useOidcStore();
    const oidcIsAuthenticated = computed(() => oidcStore.oidcIsAuthenticated);

    const isCustomerLoggedIn = computed(() => oidcIsAuthenticated.value);

    const hideContent = computed(() => false);
    const open = computed(() => false);
    const toggle = () => {};
    const closeChild = () => {};

    return {
      oidcIsAuthenticated,
      isCustomerLoggedIn,
      hideContent,
      open,
      toggle,
      closeChild,
    };
  },
});

const gtmAttr = document.documentElement.getAttribute('data-gtm');
const gtmIds = gtmAttr ? gtmAttr.split(',') : [];

if (gtmIds.length > 0) {
  app.use(
    createGtm({
      id: gtmIds,
      defer: false,
      enabled: true,
      debug: false,
      loadScript: true,
      trackOnNextTick: true,
    })
  );
}

app.use(pinia);
app.use(i18n);

const normalizeHexColor = (hex: string, fallback: string): string => {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => char + char)
          .join('')
      : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return fallback;
  }

  return `#${full}`;
};

const storeColor = normalizeHexColor(
  document.documentElement.getAttribute('data-store-color') ?? '#000000',
  '#000000'
);
const productImageBgColor = normalizeHexColor(
  document.documentElement.getAttribute('data-product-image-bg-color') ?? '#ffffff',
  '#ffffff'
);

const darkenHexColor = (hex: string, amount = 0.2): string => {
  const full = hex.replace('#', '');
  const toHex = (value: number): string =>
    `0${Math.max(0, Math.round(value * (1 - amount))).toString(16)}`.slice(-2);

  return `#${toHex(parseInt(full.slice(0, 2), 16))}${toHex(parseInt(full.slice(2, 4), 16))}${toHex(parseInt(full.slice(4, 6), 16))}`;
};

document.documentElement.style.setProperty('--store-color', storeColor);
document.documentElement.style.setProperty('--store-color__light', `${storeColor}10`);
document.documentElement.style.setProperty('--store-color__dark', darkenHexColor(storeColor));
document.documentElement.style.setProperty('--product-image-bg-color', productImageBgColor);
document.documentElement.style.setProperty('--bg-color', productImageBgColor);

app.component('VueHeader', Header);
app.component('FlashMessages', FlashMessages);
app.component('MainMenu', MainMenu);
app.component('MainMenuItem', MainMenuItem);
app.component('MainMenuSubmenu', MainMenuSubmenu);
app.component('MegaMenuPanel', MegaMenuPanel);
app.component('MobileMenu', MobileMenu);
app.component('VueFooter', Footer);
app.component('WishlistPage', WishlistPage);
app.component('LoginPage', LoginPage);
app.component('BankDataPage', BankDataPage);
app.component('VueBannerWrapper', BannerWrapper);
app.component('MarketingStats', MarketingStats);
app.component('DiscountCodeCopy', DiscountCodeCopy);
app.component('CampaignBannerPopup', CampaignBannerPopup);
app.component('RegisterPage', RegisterPage);
app.component('ForgotPasswordPage', ForgotPasswordPage);
app.component('ResetPasswordPage', ResetPasswordPage);
app.component('AccountDetailsPage', AccountDetailsPage);
app.component('ReturnRequestsPage', ReturnRequestsPage);
app.component('ReturnRequestPage', ReturnRequestPage);
app.component('ReturnRequestDetailView', ReturnRequestDetailView);
app.component('WarrantyRemedyPage', WarrantyRemedyPage);
app.component('DoubleOrderPage', DoubleOrderPage);
app.component('SplitOrderPage', SplitOrderPage);
app.component('ReturnLoginPage', ReturnLoginPage);
app.component('VueCart', CartPage);
app.component('VueSuccess', SuccessPage);
app.component('VueCheckout', CheckoutPage);
app.component('DeliveryDatePage', DeliveryDatePage);
app.component('OrdersPage', OrdersPage);
app.component('OrderDetailPage', OrderDetailPage);
app.component('OrderEditAddressPage', OrderEditAddressPage);
app.component('ProductDetailPage', ProductDetailPage);
app.component('ShopTheLookBlock', ShopTheLookBlock);
app.component('SnowChainPreselectBlock', SnowChainPreselectBlock);
app.component('RoofRackBlock', RoofRackBlock);
app.component('RoofRackPreselectBlock', RoofRackPreselectBlock);
app.component('CategoryPreselectBlock', CategoryPreselectBlock);
app.component('CalfCircumferencePreselectBlock', CalfCircumferencePreselectBlock);
app.component('CategoryPreselectBooleanBlock', CategoryPreselectBooleanBlock);
app.component('PadelRacketToolBlock', PadelRacketToolBlock);
app.component('TennisRacketToolBlock', TennisRacketToolBlock);
app.component('FilterPills', FilterPills);
app.component('BlockCarousel', BlockCarousel);
app.component('BlockCarouselSlide', BlockCarouselSlide);
app.component('BlockSlider', BlockSlider);
app.component('BlockSliderItem', BlockSliderItem);
app.component('TextCollapsable', TextCollapsable);
app.component('ContentItemLayout', ContentItemLayout);
app.component('YouTubeEmbed', YouTubeEmbed);
app.component('ProductOverview', ProductOverview);
app.component('GiftcardSaldoPage', GiftcardSaldoPage);

const oidcStore = useOidcStore();

const isOidcCallback = window.location.pathname === generateUrl('customer_oidc_callback');

if (!isOidcCallback) {
  useFlashStore().loadFromCookie();
}

async function checkAndSyncAuth() {
  await routingReady;
  await oidcStore.syncUserFromStorage();
  app.mount('#app-vue3');
}

if (isOidcCallback) {
  oidcStore
    .signinRedirectCallback()
    .then(async user => {
      await routingReady;
      await oidcStore.syncUserFromStorage();
      const stateUrl = generateUrl('customer_account_details');
      const stateFromOidc = user.state as string | undefined;
      const stateIsPath =
        typeof stateFromOidc === 'string' &&
        stateFromOidc.startsWith('/') &&
        !stateFromOidc.startsWith('//') &&
        stateFromOidc !== '/';

      let returnPath: string;
      if (stateIsPath) {
        returnPath = stateFromOidc;
      } else {
        try {
          const storedRoute = window.sessionStorage.getItem('vuex_oidc_active_route');
          const useStored =
            storedRoute &&
            storedRoute.trim() !== '' &&
            storedRoute !== '/' &&
            storedRoute.startsWith('/') &&
            !storedRoute.startsWith('//');
          if (useStored) {
            returnPath = storedRoute;
            window.sessionStorage.removeItem('vuex_oidc_active_route');
          } else {
            returnPath = stateUrl;
          }
        } catch {
          returnPath = stateUrl;
        }
      }

      if (returnPath.startsWith('/') && !returnPath.startsWith('//')) {
        window.location.href = returnPath;
      } else {
        window.location.href = stateUrl;
      }
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('OIDC callback error:', error);
      window.location.href = '/';
    });
} else {
  checkAndSyncAuth();
}
